"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.squizNodeToRemirrorNode = void 0;
const undefinedIfEmpty_1 = require("../../undefinedIfEmpty");
const Extensions_1 = require("../../../Extensions/Extensions");
const getNodeType = (node) => {
    const typeMap = {
        'link-to-matrix-asset': Extensions_1.NodeName.Text,
        'matrix-image': Extensions_1.NodeName.AssetImage,
        'dam-image': Extensions_1.NodeName.DAMImage,
        text: 'text',
    };
    const tagMap = {
        h1: 'heading',
        h2: 'heading',
        h3: 'heading',
        h4: 'heading',
        h5: 'heading',
        h6: 'heading',
        img: 'image',
        pre: 'preformatted',
        p: 'paragraph',
        ol: 'orderedList',
        li: 'listItem',
        ul: 'bulletList',
        hr: 'horizontalRule',
        table: 'table',
        tr: 'tableRow',
        th: 'tableHeaderCell',
        td: 'tableCell',
        a: Extensions_1.NodeName.Text,
        em: Extensions_1.NodeName.Text,
        span: Extensions_1.NodeName.Text,
        strong: Extensions_1.NodeName.Text,
        code: Extensions_1.NodeName.CodeBlock,
        br: Extensions_1.NodeName.hardBreak,
    };
    if (typeMap[node.type]) {
        return typeMap[node.type];
    }
    if (node.type === 'tag' && tagMap[node.tag]) {
        // This is a specific check case for tables as there are some <th> tags which need to be returned
        // as table controller cells.
        if (node.attributes?.tableControllerCell) {
            return 'tableControllerCell';
        }
        // Return regular tag for everything else
        return tagMap[node.tag];
    }
    // Unsupported node type
    throw new Error(node.type === 'tag'
        ? `Unsupported node type provided: ${node.type} (tag: ${node.tag})`
        : `Unsupported node type provided: ${node.type}`);
};
const getNodeAttributes = (node) => {
    if (node.type === 'tag' && node.tag === 'table') {
        return {
            isControllersInjected: true,
        };
    }
    if (node.type === 'tag' && (node.tag === 'th' || node.tag === 'td')) {
        return {
            colspan: parseInt(node.attributes?.colspan ?? '1'),
            rowspan: parseInt(node.attributes?.rowspan ?? '1'),
            colwidth: node.attributes?.colwidth ? [parseInt(node.attributes.colwidth)] : null,
            background: null,
        };
    }
    if (node.type === 'tag' && node.tag === 'img') {
        return {
            alt: node.attributes?.alt,
            height: node.attributes?.height,
            width: node.attributes?.width,
            src: node.attributes?.src,
            title: node.attributes?.title,
        };
    }
    else if (node.type === 'matrix-image') {
        return {
            matrixAssetId: node.matrixAssetId,
            matrixDomain: node.matrixDomain,
            matrixIdentifier: node.matrixIdentifier,
        };
    }
    else if (node.type === 'dam-image') {
        return {
            damObjectId: node.damObjectId,
            damSystemIdentifier: node.damSystemIdentifier,
            damSystemType: node.damSystemType,
            damAdditional: node.damAdditional ? JSON.stringify(node.damAdditional) : undefined,
        };
    }
    else if (node.type === 'tag') {
        return {
            nodeIndent: null,
            nodeTextAlignment: node.formattingOptions?.alignment || null,
            nodeLineHeight: null,
            style: '',
            level: node.tag?.startsWith('h') ? parseInt(node.tag.substring(1)) : undefined,
        };
    }
    return {};
};
const getNodeMarks = (node) => {
    const marks = [];
    if (node.type === 'tag' && node.tag === 'a') {
        marks.push({
            type: Extensions_1.MarkName.Link,
            attrs: {
                href: node.attributes?.href,
                target: node.attributes?.target ?? null,
                auto: false,
                title: node.attributes?.title ?? null,
            },
        });
    }
    else if (node.type === 'link-to-matrix-asset') {
        marks.push({
            type: Extensions_1.MarkName.AssetLink,
            attrs: {
                matrixAssetId: node.matrixAssetId,
                matrixDomain: node.matrixDomain,
                matrixIdentifier: node.matrixIdentifier,
                target: node.target,
            },
        });
    }
    else if (node.type === 'tag' && node.tag === 'strong') {
        marks.push({ type: 'bold' });
    }
    else if (node.type === 'tag' && node.tag === 'em') {
        marks.push({ type: 'italic' });
    }
    // Handle font formatting
    if ('font' in node && node.font !== undefined) {
        for (const [type, enabled] of Object.entries(node.font)) {
            if (enabled) {
                if (type === 'bold' || type === 'italics' || type === 'underline') {
                    marks.push({ type: type === 'italics' ? 'italic' : type });
                }
                else {
                    throw new Error(`Unsupported mark provided: ${type}`);
                }
            }
        }
    }
    return marks;
};
const unwrapNodeIfNeeded = (node) => {
    if (node.type === 'text' && node.content?.length) {
        return node.content.map((child) => {
            return {
                ...child,
                marks: [...(child.marks || []), ...(node.marks || [])],
            };
        });
    }
    return [node];
};
const formatNode = (node) => {
    const children = [];
    if ('children' in node) {
        node.children?.forEach((child) => {
            children.push(...formatNode(child));
        });
    }
    return unwrapNodeIfNeeded({
        type: getNodeType(node),
        attrs: (0, undefinedIfEmpty_1.undefinedIfEmpty)(getNodeAttributes(node)),
        marks: (0, undefinedIfEmpty_1.undefinedIfEmpty)(getNodeMarks(node)),
        text: node.type === 'text' ? node.value : undefined,
        content: (0, undefinedIfEmpty_1.undefinedIfEmpty)(children),
    });
};
/**
 * Converts Squiz component JSON structure to Remirror node JSON structure.
 * @param {FormattedText} nodes Squiz nodes to convert to Remirror.
 * @export
 * @returns {RemirrorJSON} The converted Remirror JSON.
 */
const squizNodeToRemirrorNode = (nodes = []) => {
    let children = [];
    nodes.forEach((node) => {
        try {
            children.push(...formatNode(node));
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            children.push({
                type: 'paragraph',
                content: [
                    {
                        type: Extensions_1.NodeName.Unsupported,
                        attrs: {
                            originalNode: node,
                            errorMessage,
                        },
                    },
                ],
            });
        }
    });
    if (children.find((child) => child.type === 'text')) {
        children = [{ type: 'paragraph', content: children }];
    }
    return { type: 'doc', content: children };
};
exports.squizNodeToRemirrorNode = squizNodeToRemirrorNode;
